import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { PressEvent } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonKind, type ButtonProps, type ButtonSize } from '@gitbook/spine-react';

import { createPalette, PaletteButton, type PaletteDef } from '../Palette';

export type ResponsiveRowButton = Omit<ButtonProps, 'kind' | 'size' | 'onPress'> & {
    key: string;
    /**
     * onPress callback with an optional event in case we open it from the palette
     */
    onPress: (event?: PressEvent) => void;
    /**
     * Whether the button should be in the palette by default
     */
    inPalette?: boolean;
};

export interface ResponsiveButtonsRowProps {
    /** Ordered list of buttons to render */
    buttons: ResponsiveRowButton[];
    /** Optional className for the root container */
    className?: string;
    /** Kind of buttons */
    kind?: ButtonKind;
    /** Size of the buttons */
    size?: ButtonSize;
}

/**
 * Row of buttons that automatically moves overflowing items into a "More" menu.
 */
export const ResponsiveButtonsRow = React.memo(function ResponsiveButtonsRow(
    props: ResponsiveButtonsRowProps
) {
    const { buttons, className, kind, size } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const prevWidthRef = useRef(0);
    const responsiveButtons = buttons.filter((btn) => !btn.inPalette);
    const alwaysInPaletteButtons = buttons.filter((btn) => btn.inPalette);

    const [visibleCount, setVisibleCount] = useState(responsiveButtons.length);

    // Reset the visible count when the buttons change
    useEffect(() => {
        setVisibleCount(responsiveButtons.length);
    }, [responsiveButtons.length]);

    // Update the visible count when the window is resized
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handleResize = () => {
            const width = el.clientWidth;
            const prevWidth = prevWidthRef.current;
            prevWidthRef.current = width;

            if (width > prevWidth && visibleCount < responsiveButtons.length) {
                setVisibleCount(responsiveButtons.length);
                return;
            }

            const lastChild = el.children[el.children.length - 1];
            if (!lastChild) {
                return;
            }

            // Hide one button if overflowing
            if (lastChild.getBoundingClientRect().right > el.getBoundingClientRect().right) {
                setVisibleCount((prev) => Math.max(0, prev - 1));
            }
        };

        handleResize();

        const observer = new ResizeObserver(() => {
            requestAnimationFrame(handleResize);
        });
        observer.observe(el);

        return () => {
            observer.disconnect();
        };
    }, [responsiveButtons.length, visibleCount]);

    const { visibleButtons, overflowButtons } = useMemo(() => {
        return {
            visibleButtons: responsiveButtons.slice(0, visibleCount),
            overflowButtons: responsiveButtons.slice(visibleCount),
        };
    }, [responsiveButtons, visibleCount]);

    const generatePalette = (): PaletteDef => {
        return createPalette({
            groups: [
                ...(alwaysInPaletteButtons.length > 0
                    ? [
                          {
                              key: 'persistent',
                              commands: alwaysInPaletteButtons.map((item) => ({
                                  key: item.key,
                                  title: item.label,
                                  icon: item.leadingIcon || item.trailingIcon,
                                  trigger: () => item.onPress(),
                              })),
                          },
                      ]
                    : []),
                ...(overflowButtons.length > 0
                    ? [
                          {
                              key: 'overflow',
                              commands: overflowButtons
                                  .map((item) => ({
                                      key: item.key,
                                      title: item.label,
                                      icon: item.leadingIcon || item.trailingIcon,
                                      trigger: () => item.onPress(),
                                  }))
                                  .reverse(),
                          },
                      ]
                    : []),
            ].filter(Boolean),
        });
    };

    return (
        <div
            ref={containerRef}
            className={clsx('flex w-full min-w-0 items-center gap-x-1', className)}
        >
            {visibleButtons.map((item) => (
                <Button {...item} kind={kind} size={size} key={item.key} />
            ))}

            {overflowButtons.length > 0 || alwaysInPaletteButtons.length > 0 ? (
                <div className="relative flex-shrink-0">
                    <PaletteButton
                        palette={generatePalette}
                        label="More"
                        iconOnly
                        leadingIcon={Icon.MoreHorizontal}
                        kind={kind}
                        size={size}
                    />
                </div>
            ) : null}
        </div>
    );
});
